import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ScanLine } from 'lucide-react';
import { Dialog } from '@/shared/ui/dialogs/Dialog';
import { Button } from '@/shared/ui/buttons/Button';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiWarehouseProductPost } from '@/entities/warehouse';
import { extractErrorMessage, isApiError } from '@/shared/api/errors';

type Props = {
  open: boolean;
  onClose: () => void;
};

type BufferEntry = { key: string; ts: number };

const MIN_BUFFER = 5;
const MAX_GAP_MS = 50;
const RESET_IDLE_MS = 200;

const looksLikeDuplicateTrekId = (raw: unknown): boolean => {
  if (!raw) return false;
  let pool = '';
  if (typeof raw === 'string') {
    pool = raw;
  } else if (typeof raw === 'object') {
    try {
      pool = JSON.stringify(raw);
    } catch {
      pool = '';
    }
  }
  const lower = pool.toLowerCase();
  if (!lower.includes('trek')) return false;
  return (
    lower.includes('already') ||
    lower.includes('mavjud') ||
    lower.includes('mavzhud') ||
    lower.includes('exists') ||
    lower.includes('duplicate') ||
    lower.includes('avval') ||
    lower.includes('уже') ||
    lower.includes('существует')
  );
};

export const FeaturesWarehouseScannerDialog = ({ open, onClose }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();

  const bufferRef = useRef<BufferEntry[]>([]);
  const idleTimerRef = useRef<number | null>(null);
  const pendingRef = useRef(false);
  const waitToastShownRef = useRef(false);

  useEffect(() => {
    if (!open) return;

    bufferRef.current = [];
    pendingRef.current = false;
    waitToastShownRef.current = false;

    const clearIdleTimer = (): void => {
      if (idleTimerRef.current !== null) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };

    const scheduleIdleReset = (): void => {
      clearIdleTimer();
      idleTimerRef.current = window.setTimeout(() => {
        bufferRef.current = [];
        idleTimerRef.current = null;
      }, RESET_IDLE_MS);
    };

    const submit = async (trekId: string): Promise<void> => {
      if (!trekId) return;
      if (pendingRef.current) {
        if (!waitToastShownRef.current) {
          waitToastShownRef.current = true;
          toast.info(t('warehouse.scanner.toast.wait'));
          window.setTimeout(() => {
            waitToastShownRef.current = false;
          }, 1500);
        }
        return;
      }
      pendingRef.current = true;
      try {
        await apiWarehouseProductPost({ trek_id: trekId });
        toast.success(t('warehouse.scanner.toast.added', { trekId }));
        qc.invalidateQueries({ queryKey: queryKeys.warehouseProducts.all });
        qc.invalidateQueries({ queryKey: queryKeys.warehouse.all });
        qc.invalidateQueries({ queryKey: queryKeys.parcels.all });
      } catch (err) {
        const data = isApiError(err) ? err.response?.data : undefined;
        const status = isApiError(err) ? err.response?.status : undefined;
        const message = extractErrorMessage(err);
        const dup =
          status === 400 &&
          (looksLikeDuplicateTrekId(data) || looksLikeDuplicateTrekId(message));
        if (dup) {
          toast.error(t('warehouse.scanner.toast.duplicate', { trekId }));
        } else {
          toast.error(message);
        }
      } finally {
        pendingRef.current = false;
      }
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Enter') {
        const buf = bufferRef.current;
        if (buf.length >= MIN_BUFFER) {
          let maxGap = 0;
          for (let i = 1; i < buf.length; i += 1) {
            const prev = buf[i - 1];
            const curr = buf[i];
            if (!prev || !curr) continue;
            const gap = curr.ts - prev.ts;
            if (gap > maxGap) maxGap = gap;
          }
          if (maxGap <= MAX_GAP_MS) {
            // Normalise to upper-case — owner rule: every trek_id capture
            // path stores canonical upper-case so the backend never sees a
            // lowercase variant from a noisy scanner.
            const trekId = buf.map((b) => b.key).join('').trim().toUpperCase();
            bufferRef.current = [];
            clearIdleTimer();
            event.preventDefault();
            void submit(trekId);
            return;
          }
        }
        bufferRef.current = [];
        clearIdleTimer();
        event.preventDefault();
        return;
      }

      if (event.key.length !== 1) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      event.preventDefault();

      const now = performance.now();
      const buf = bufferRef.current;
      const last = buf.length > 0 ? buf[buf.length - 1] : undefined;
      if (last && now - last.ts > RESET_IDLE_MS) {
        bufferRef.current = [{ key: event.key, ts: now }];
      } else {
        buf.push({ key: event.key, ts: now });
      }
      scheduleIdleReset();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      clearIdleTimer();
      bufferRef.current = [];
    };
  }, [open, qc, t, toast]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={t('warehouse.scanner.title')}
      closeOnBackdrop={false}
      footer={
        <Button variant="outline" onClick={onClose}>
          {t('warehouse.scanner.close')}
        </Button>
      }
    >
      <div className="flex flex-col items-center justify-center gap-6 py-10">
        <h2 className="text-center text-2xl font-bold text-ink">
          {t('warehouse.scanner.titleOnly')}
        </h2>
        <ScanLine
          className="h-24 w-24 text-brand-600"
          strokeWidth={1.5}
          aria-hidden
        />
      </div>
    </Dialog>
  );
};
